import { upperFirst } from 'lodash';
import { rem } from 'polished';
import { useTranslation } from 'react-i18next';
import Media from 'react-media';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import SurveyBg from '../../assets/survey-bg.jpg';
import SurveyMobileBg from '../../assets/survey-mobile-bg.png';
import NavBar from '../../components/NavBar';
import SurveyForm from '../../components/Survey/SurveyForm';
import SurveyNotFound from '../../components/Survey/SurveyNotFound';
import SurveySubmitted from '../../components/Survey/SurveySubmitted';
import { useToastContext } from '../../contexts/ToastProvider';
import surveyApi from '../../services/api/survey';
import {
  StyledContainer,
  StyledSpinner,
  VisualStackContainer,
} from './SurveyAnswer.styles';

const SurveyAnswer = (): JSX.Element | null => {
  const { t } = useTranslation(['error']);
  const { surveyId } = useParams();
  const toast = useToastContext();

  const { status, isLoading } = useQuery({
    queryKey: ['survey', surveyId],
    queryFn: () => surveyApi.isSurveyValid(surveyId as string),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const {
    mutateAsync,
    isLoading: submitSurveyLoading,
    status: submitSurveyStatus,
  } = useMutation(surveyApi.submitSurvey, {
    onError: () => {
      toast.error(upperFirst(t('error:error')));
    },
  });

  if (submitSurveyStatus === 'success') {
    return (
      <StyledContainer>
        <NavBar stepper={false} />
        {submitSurveyStatus === 'success' && <SurveySubmitted />}
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <NavBar stepper={false} />
      {isLoading && <StyledSpinner size={60} />}
      {status === 'error' && <SurveyNotFound />}
      {status === 'success' && (
        <>
          <VisualStackContainer>
            <Media
              queries={{
                mobile: `(max-width: ${rem('575.98px')}`,
                desktop: `(min-width: ${rem('575.98px')}`,
              }}
            >
              {matches => (
                <>
                  {matches.desktop && (
                    <img src={SurveyBg} width="100%" height="100%" />
                  )}
                  {matches.mobile && (
                    <img src={SurveyMobileBg} width="100%" height="100%" />
                  )}
                </>
              )}
            </Media>
          </VisualStackContainer>
          <SurveyForm
            mutateAsync={mutateAsync}
            isLoading={submitSurveyLoading}
          />
        </>
      )}
    </StyledContainer>
  );
};

export default SurveyAnswer;
